/**
 * Shared Firebase Configuration
 * This file contains the Firebase configuration that is used across
 * both the main application and the service worker.
 */

// Validate required environment variables
const requiredEnvVars = [
  'REACT_APP_FIREBASE_API_KEY',
  'REACT_APP_FIREBASE_AUTH_DOMAIN',
  'REACT_APP_FIREBASE_PROJECT_ID',
  'REACT_APP_FIREBASE_STORAGE_BUCKET',
  'REACT_APP_FIREBASE_MESSAGING_SENDER_ID',
  'REACT_APP_FIREBASE_APP_ID',
  'REACT_APP_FIREBASE_VAPID_KEY'
];

const missingVars = requiredEnvVars.filter(varName => !process.env[varName]);
if (missingVars.length > 0) {
  throw new Error(
    `Missing required Firebase environment variables: ${missingVars.join(', ')}. ` +
    'Please check your .env file and ensure all required variables are set.'
  );
}

// Firebase configuration object
export const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

// VAPID key for push notifications
export const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;

// Notification configuration
export const notificationConfig = {
  defaultIcon: "/logo192.png",
  defaultBadge: "/logo192.png",
  defaultTitle: "VibeLinks",
  defaultBody: "You have a new notification",
  vibrationPattern: [200, 100, 200],
  actions: [
    {
      action: "open",
      title: "Open",
      icon: "/logo192.png",
    },
    {
      action: "close",
      title: "Close",
    },
  ],
};

// Route mapping for notifications
export const notificationRoutes = {
  VIBE_JOIN: (id) => (id ? `/vibe/${id}` : "/notifications"),
  VIBE_CANCELLED: (id) => (id ? `/vibe/${id}` : "/my-vibes"),
  VIBE_CREATED: (id) => (id ? `/vibe/${id}` : "/my-vibes"),
  VIBE_REMINDER: (id) => (id ? `/vibe/${id}` : "/calendar"),
  DEFAULT: () => "/notifications",
};

/**
 * Extract notification ID from payload data
 * @param {Object} data - Notification data payload
 * @returns {string|null} - Extracted ID or null
 */
export const extractNotificationId = (data) => {
  if (!data) return null;

  const possibleIdFields = [
    "id",
    "vibe_id",
    "vibeId",
    "VibeId",
    "VIBE_ID",
    "event_id",
    "eventId",
    "data",
  ];

  for (const field of possibleIdFields) {
    const value = data[field];
    if (value != null) {
      // Check if data field contains a numeric string
      if (field === "data" && /^\d+$/.test(String(value))) {
        return String(value);
      } else if (field !== "data") {
        return String(value);
      }
    }
  }

  return null;
};

/**
 * Build notification route based on type and ID
 * @param {Object} data - Notification data payload
 * @returns {string} - Route to navigate to
 */
export const buildNotificationRoute = (data) => {
  if (!data) return notificationRoutes.DEFAULT();

  // Check for explicit URL first
  const explicitUrl = data.url || data.URL || data.deep_link || data.deeplink;
  if (explicitUrl && typeof explicitUrl === "string") {
    return explicitUrl;
  }

  // Get notification type and ID
  const type = (data.type || data.notification_type || "")
    .toString()
    .toUpperCase();
  const id = extractNotificationId(data);

  // Use route mapping
  const routeBuilder = notificationRoutes[type] || notificationRoutes.DEFAULT;
  return routeBuilder(id);
};
